import { useParams, Link, useNavigate } from "react-router-dom";
import { products } from "@/data/products";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
    CheckCircle2,
    ShoppingCart,
    ArrowLeft,
    Sparkles,
    Monitor,
    Download,
    ShieldCheck,
    Zap,
    PlayCircle,
    X
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

const AIProgramDetail = () => {
    const { id } = useParams();
    const { t } = useLanguage();
    const { toast } = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { cart, addToCart, removeFromCart, totalPrice, showCart, setShowCart } = useCart();
    const product = products.find(p => p.id === Number(id));

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">{t("ไม่พบโปรแกรมนี้", "Program not found")}</h1>
                    <Link to="/ai-program">
                        <Button variant="outline">{t("กลับไปหน้าโปรแกรม", "Back to Programs")}</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const handleAddToCart = () => {
        const added = addToCart(product);
        if (added) {
            toast({
                title: t("เพิ่มลงตะกร้าแล้ว", "Added to cart"),
                description: t(`${product.name} ถูกเพิ่มลงตะกร้าของคุณแล้ว`, `${product.name} has been added to your cart`),
            });
            setShowCart(true);
        } else {
            toast({
                title: t("มีในตะกร้าแล้ว", "Already in cart"),
                description: t(`${product.name} อยู่ในตะกร้าของคุณแล้ว`, `${product.name} is already in your cart`),
            });
        }
    };

    const handleCheckout = () => {
        if (!user) {
            toast({
                variant: "destructive",
                title: t("กรุณาเข้าสู่ระบบ", "Please login"),
                description: t("คุณต้องเข้าสู่ระบบก่อนดำเนินการชำระเงิน", "You must login before proceeding to checkout")
            });
            setShowCart(false);
            navigate("/login");
            return;
        }

        setShowCart(false);
        navigate("/ai-program");
        // Small delay to ensure navigation completes, then open cart
        setTimeout(() => setShowCart(true), 100);
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            {/* Floating Cart Button */}
            <div className="fixed top-20 right-4 z-40">
                <Button
                    variant="glow"
                    size="lg"
                    className="gap-2 shadow-lg"
                    onClick={() => setShowCart(true)}
                >
                    <ShoppingCart className="h-5 w-5" />
                    {t("ตะกร้า", "Cart")} ({cart.length})
                    {cart.length > 0 && (
                        <Badge variant="secondary" className="ml-1">
                            ฿{totalPrice.toLocaleString()}
                        </Badge>
                    )}
                </Button>
            </div>

            {/* Cart Sidebar */}
            {showCart && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setShowCart(false)}>
                    <div
                        className="absolute right-0 top-0 h-full w-full max-w-md bg-background border-l border-border p-6 overflow-y-auto"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold">{t("ตะกร้าสินค้า", "Shopping Cart")}</h2>
                            <Button variant="ghost" size="icon" onClick={() => setShowCart(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {cart.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                <p>{t("ตะกร้าว่างเปล่า", "Cart is empty")}</p>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-4 mb-6">
                                    {cart.map((item) => (
                                        <Card key={item.id} className="bg-muted/30 border-border/50">
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-3">
                                                        <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                                            {item.icon}
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold">{item.name}</h3>
                                                            <p className="text-sm text-muted-foreground">
                                                                {t(`ใช้ได้ ${item.devices} เครื่อง`, `${item.devices} devices`)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeFromCart(item.id)}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                <div className="text-right mt-2">
                                                    <span className="font-bold text-primary">
                                                        ฿{item.price.toLocaleString()}
                                                    </span>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>

                                <div className="border-t border-border pt-4">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-lg">{t("ยอดรวม", "Total")}</span>
                                        <span className="text-2xl font-bold text-gradient">
                                            ฿{totalPrice.toLocaleString()}
                                        </span>
                                    </div>
                                    <Button variant="glow" className="w-full gap-2" onClick={handleCheckout}>
                                        <ShoppingCart className="h-4 w-4" />
                                        {t("ไปชำระเงิน", "Proceed to Checkout")}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <main className="pt-24 pb-16">
                <div className="container mx-auto px-4">
                    {/* Back Button */}
                    <Link to="/ai-program" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
                        <ArrowLeft className="h-4 w-4" />
                        <span>{t("กลับไปหน้าโปรแกรม", "Back to Programs")}</span>
                    </Link>

                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
                        {/* Left: Content */}
                        <div className="space-y-8">
                            <div>
                                <Badge variant="secondary" className="mb-4 px-3 py-1">
                                    <Sparkles className="h-3 w-3 mr-1 text-primary" />
                                    {t("นวัตกรรม AI ล่าสุด", "Latest AI Innovation")}
                                </Badge>
                                <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                                    <span className="text-gradient">{product.name}</span>
                                </h1>
                                <p className="text-xl text-muted-foreground leading-relaxed">
                                    {t(product.longDescriptionTh, product.longDescriptionEn)}
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <Button size="lg" className="h-14 px-8 gap-2 text-lg font-bold" onClick={handleAddToCart}>
                                    <ShoppingCart className="h-5 w-5" />
                                    {t("สั่งซื้อตอนนี้", "Buy Now")} - ฿{product.price.toLocaleString()}
                                </Button>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-xl">
                                    <Monitor className="h-4 w-4" />
                                    {t(`รองรับ ${product.devices} เครื่อง`, `Supports ${product.devices} devices`)}
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4 pt-4">
                                {[
                                    { icon: <Zap className="h-5 w-5 text-yellow-500" />, textTh: "ทำงานรวดเร็วด้วยบอท", textEn: "Fast bot automation" },
                                    { icon: <ShieldCheck className="h-5 w-5 text-green-500" />, textTh: "ปลอดภัย 100%", textEn: "100% Secure & Safe" },
                                    { icon: <Download className="h-5 w-5 text-blue-500" />, textTh: "อัปเดตฟรีตลอดชีพ", textEn: "Lifetime Free Updates" },
                                    { icon: <CheckCircle2 className="h-5 w-5 text-primary" />, textTh: "ซัพพอร์ตมืออาชีพ", textEn: "Professional Support" },
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-glass transition-all hover:border-primary/30">
                                        {item.icon}
                                        <span className="text-sm font-medium">{t(item.textTh, item.textEn)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: Media Showcase */}
                        <div className="relative group">
                            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-purple-500/20 rounded-[2rem] blur-2xl opacity-50 transition duration-1000 group-hover:opacity-75" />
                            <Card className="relative overflow-hidden border-white/20 bg-background/50 backdrop-blur-xl rounded-[2rem] shadow-2xl overflow-hidden aspect-video flex items-center justify-center">
                                {product.videoUrl ? (
                                    <iframe
                                        className="w-full h-full"
                                        src={product.videoUrl}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                ) : (
                                    <img
                                        src={product.imageUrls[0]}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
                                    />
                                )}
                                {!product.videoUrl && (
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-white scale-90 group-hover:scale-100 transition duration-500">
                                            <PlayCircle className="h-8 w-8" />
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </div>
                    </div>

                    {/* Detailed Features Section */}
                    <section className="py-20 border-t border-border/50">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("ทำไมโปรแกรมนี้ถึงคู่ควรกับคุณ?", "Why is this program for you?")}</h2>
                            <p className="text-muted-foreground">{t("รวมเทคโนโลยีที่ล้ำหน้าที่สุดเพื่อความสำเร็จของคุณ", "Unified cutting-edge technologies for your success")}</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {(t(product.featuresTh.join("|||"), product.featuresEn.join("|||"))).split("|||").map((feature, idx) => (
                                <Card key={idx} className="p-8 bg-glass border-primary/10 hover:border-primary/30 transition-all hover:-translate-y-1">
                                    <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6">
                                        <CheckCircle2 className="h-6 w-6" />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3">{feature}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {t(
                                            "ผ่านการทดสอบและปรับปรุงให้มีประสิทธิภาพสูงสุด เพื่อให้คุณได้ผลลัพธ์ที่ดีที่สุดในทุกการใช้งาน",
                                            "Tested and optimized for maximum efficiency to ensure you get the best results in every use."
                                        )}
                                    </p>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* Screenshots / Examples Grid */}
                    <section className="py-20">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <h2 className="text-3xl font-bold mb-2">{t("ผลลัพธ์ที่คุณจะได้รับ", "The Results You'll Get")}</h2>
                                <p className="text-muted-foreground">{t("ตัวอย่างการทำงานจริงของโปรแกรมที่ทำให้ชีวิตง่ายขึ้น", "Real-world examples of how this program simplifies your life")}</p>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            {product.imageUrls.map((url, idx) => (
                                <div key={idx} className="rounded-[1.5rem] overflow-hidden border border-border/50 shadow-xl aspect-video group">
                                    <img src={url} alt={`Screenshot ${idx}`} className="w-full h-full object-cover transition duration-700 group-hover:scale-110" />
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* CTA Bottom Section */}
                    <section className="py-20">
                        <Card className="rounded-[3rem] overflow-hidden bg-primary/5 border-primary/20 p-8 md:p-16 text-center relative">
                            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top,rgba(var(--primary-rgb),0.15),transparent)] pointer-events-none" />
                            <h2 className="text-3xl md:text-5xl font-bold mb-6">{t("พร้อมเริ่มต้นความสำเร็จหรือยัง?", "Ready to start your success?")}</h2>
                            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                                {t("อย่าปล่อยให้คู่แข่งแซงหน้า เริ่มต้นใช้งานเครื่องมือ AI ที่ดีที่สุดวันนี้", "Don't let competitors get ahead. Start using the best AI tools today.")}
                            </p>
                            <Button size="lg" className="h-16 px-12 gap-3 text-xl font-black rounded-2xl shadow-2xl shadow-primary/20 active:scale-95 transition-all" onClick={handleAddToCart}>
                                <ShoppingCart className="h-6 w-6" />
                                {t("ต้องการโปรแกรมนี้ทันที", "Grab This Program Now")}
                            </Button>
                            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-muted-foreground font-medium">
                                <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-green-500" /> {t("รับประกันความพึงพอใจ", "Satisfaction Guaranteed")}</span>
                                <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-yellow-500" /> {t("ติดตั้งง่าย ไม่ยุ่งยาก", "Easy Installation")}</span>
                            </div>
                        </Card>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AIProgramDetail;
